// Shared compass projection — used by both the live Quiz UI
// (src/components/Quiz/Quiz.tsx) and the OG image endpoint
// (api/og/quiz-result.tsx) so the dot positions in the share image
// match exactly what the user sees on the page.
//
// The compass has two editorially-fixed axes:
//   X  Stewardship (-1) ↔ Transformation (+1)     — GS axis
//   Y  Self-reliance (-1) ↔ Collective bond (+1)  — PA axis
//
// Each question contributes to one axis with a signed weight in
// [-1, +1]. The voter / MP is plotted at the average of every answer
// they've given, scaled so the most-extreme MP lands near ±0.85 (so
// nothing crowds the compass edge). Voters can drift further than ±1
// if they're more polarised than any MP — capped to ±1 only by the
// rendering layer if needed.

export interface QuizQuestionForProjection {
  id: string;
  axis: 'GS' | 'PA';
  growth: number;
  state: number;
}

export type AnswerMap = Record<string, number>;
export type AnswersByMP = Record<string, AnswerMap>;

export interface Position {
  x: number;
  y: number;
}

export interface Projection {
  mpPos: Record<string, Position>;
  voterPos: Position;
  /** The scale factor that was applied to raw projections to keep the
   *  most-extreme MP near ±0.85. Useful for plotting an externally
   *  computed raw voter position on the same compass. */
  scale: number;
}

function signOf(n: number): -1 | 0 | 1 {
  if (n > 0) return 1;
  if (n < 0) return -1;
  return 0;
}

/** Project a single set of 1-5 Likert answers onto the GS/PA compass. */
export function projectAnswers(
  questions: QuizQuestionForProjection[],
  ans: AnswerMap,
): Position {
  let xSum = 0;
  let xN = 0;
  let ySum = 0;
  let yN = 0;
  for (const q of questions) {
    const v = ans[q.id];
    if (v == null) continue;
    const n = (v - 3) / 2; // 1..5 → -1..+1
    if (q.axis === 'GS') {
      const s = signOf(q.growth);
      if (s === 0) continue;
      xSum += n * s;
      xN++;
    } else if (q.axis === 'PA') {
      const s = signOf(q.state);
      if (s === 0) continue;
      ySum += n * s;
      yN++;
    }
  }
  return {
    x: xN ? xSum / xN : 0,
    y: yN ? ySum / yN : 0,
  };
}

/** Project every MP and the voter onto the compass with a shared scale.
 *  Only MPs with at least one answer are returned. */
export function buildProjection(
  questions: QuizQuestionForProjection[],
  answers: AnswersByMP,
  voterAnswers: AnswerMap,
): Projection {
  const mpIds = Object.keys(answers).filter(
    (id) => Object.keys(answers[id] ?? {}).length > 0,
  );

  const mpPosRaw: Record<string, Position> = {};
  let maxAbs = 1e-6;
  for (const mpid of mpIds) {
    const p = projectAnswers(questions, answers[mpid] ?? {});
    mpPosRaw[mpid] = p;
    maxAbs = Math.max(maxAbs, Math.abs(p.x), Math.abs(p.y));
  }
  const voterPosRaw = projectAnswers(questions, voterAnswers);

  // Rescale based on MPs only — voter can drift past ±0.85 if their
  // answers are more polarised than any MP's. Cap the scale at 2
  // (never compress) — if every MP already sits near ±1, leave the
  // raw positions alone.
  const scale = maxAbs > 0 ? Math.min(2, 0.85 / maxAbs) : 1;
  const mpPos: Record<string, Position> = {};
  for (const mpid of mpIds) {
    mpPos[mpid] = {
      x: mpPosRaw[mpid].x * scale,
      y: mpPosRaw[mpid].y * scale,
    };
  }

  return {
    mpPos,
    voterPos: { x: voterPosRaw.x * scale, y: voterPosRaw.y * scale },
    scale,
  };
}
